import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/system";
import DashboardLayout from "../../components/SharedLayouts/DashboardLayout";
import BackArrow2 from "../../components/BackArrow2";
import Loader from "../../components/SharedLayouts/Loader";
import CustomButton from "../../components/Buttons/Button";
import GridTable from "../../components/table/gridTable";
import DebouncedSearchInput from "../../components/table/DebouncedSearchInput";
import ExportCsvTable from "../../components/Export/ExportCsvTable";
import UpdateStockPriceModal from "../../components/modal/UpdateStockPriceModal";
import ConfigureStockPurificationModal from "../../components/modal/ConfigureStockPurificationModal";
import PayDividendModal from "../../components/modal/PayDividendModal";
import ToggleStockActiveModal from "../../components/modal/ToggleStockActiveModal";
import DeleteStockModal from "../../components/modal/DeleteStockModal";
import PlaceOrderForUserModal from "../../components/modal/PlaceOrderForUserModal";
import SellStockForUserModal from "../../components/modal/SellStockForUserModal";
import { getStock } from "../../store/actions/stocks/getStock";
import { getStockPriceHistory } from "../../store/actions/stocks/getStockPriceHistory";
import { getStockDividendPayouts } from "../../store/actions/stocks/getStockDividendPayouts";
import { getStockInvestors } from "../../store/actions/stocks/getStockInvestors";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";

const DetailItem = ({ label, value }) => (
  <div className="border border-[#EAECF0] rounded-lg p-4">
    <p className="text-xs text-[#475467] mb-1">{label}</p>
    <p className="font-[600] text-base break-words">{value ?? "-"}</p>
  </div>
);

const feeLabels = {
  broker_commission_pct: "Broker Commission %",
  broker_commission_vat_pct: "Broker Commission VAT %",
  sec_fee_pct: "SEC Fee %",
  sec_vat_pct: "SEC VAT %",
  cscs_vat_pct: "CSCS VAT %",
  sms_alert_cscs_fee_flat: "SMS Alert CSCS Fee (flat)",
  sms_alert_cscs_vat_pct: "SMS Alert CSCS VAT %",
  nse_fee_pct: "NSE Fee %",
  cscs_fee_pct: "CSCS Fee %",
  nse_vat_pct: "NSE VAT %",
  stamp_duty_pct: "Stamp Duty %",
};

function StockDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [showUpdatePrice, setShowUpdatePrice] = useState(false);
  const [showPurification, setShowPurification] = useState(false);
  const [showPayDividend, setShowPayDividend] = useState(false);
  const [historyTab, setHistoryTab] = useState("price");
  const [showToggle, setShowToggle] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showPlaceOrder, setShowPlaceOrder] = useState(false);
  const [showSellOrder, setShowSellOrder] = useState(false);

  const stock = useSelector((state) => state?.stocks?.stock);
  const loading = useSelector((state) => state?.stocks?.getStockPending);
  const priceHistory =
    useSelector((state) => state?.stocks?.priceHistory) || [];
  const priceHistoryPending = useSelector(
    (state) => state?.stocks?.priceHistoryPending,
  );
  const dividendPayouts =
    useSelector((state) => state?.stocks?.dividendPayouts) || [];
  const dividendPayoutsPending = useSelector(
    (state) => state?.stocks?.dividendPayoutsPending,
  );
  const stockInvestors =
    useSelector((state) => state?.stocks?.stockInvestors) || [];
  const stockInvestorsSummary = useSelector(
    (state) => state?.stocks?.stockInvestorsSummary,
  );
  const stockInvestorsPending = useSelector(
    (state) => state?.stocks?.stockInvestorsPending,
  );

  useEffect(() => {
    dispatch(getStock(id));
    dispatch(getStockPriceHistory(id));
    dispatch(getStockDividendPayouts({ id }));
    dispatch(getStockInvestors({ id }));
  }, [dispatch, id]);

  const currencySymbol = getCurrencySymbol(stock?.currency);

  const historyColumns = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "price",
      headerName: "Price",
      width: 150,
      renderCell: (params) =>
        params.row?.price != null
          ? `${currencySymbol} ${Number(params.row.price).toFixed(2)}`
          : `${currencySymbol} -`,
    },
    {
      field: "percent_change",
      headerName: "% Change",
      width: 120,
      renderCell: (params) =>
        `${Number(params.row?.percent_change ?? 0).toFixed(2)}%`,
    },
    {
      field: "trend",
      headerName: "Trend",
      width: 110,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.trend || "-"}
        </span>
      ),
    },
    { field: "available_volume", headerName: "Available Volume", width: 160 },
    {
      field: "side",
      headerName: "Side",
      width: 110,
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>
          {params.row?.side || "-"}
        </span>
      ),
    },
    {
      field: "donation",
      headerName: "Donation",
      width: 120,
      renderCell: (params) => (params.row?.side === "sell" ? 0 : "N/A"),
    },
    {
      field: "recorded_at",
      headerName: "Recorded At",
      width: 200,
      renderCell: (params) =>
        params.row?.recorded_at
          ? new Date(params.row.recorded_at).toLocaleString()
          : "-",
    },
  ];

  const dividendStatusStyle = (status) => {
    const value = (status || "").toLowerCase();
    if (value === "credited") {
      return { color: "#067647", backgroundColor: "#ECFDF3" };
    }
    if (value === "failed" || value === "reversed") {
      return { color: "#B42318", backgroundColor: "#FECDCA" };
    }
    return { color: "#B54708", backgroundColor: "#FFFAEB" };
  };

  const formatMoney = (currency, value) =>
    value != null
      ? `${getCurrencySymbol(currency)} ${Number(value).toLocaleString()}`
      : "-";

  const dividendColumns = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "user_name",
      headerName: "User",
      width: 200,
      renderCell: (params) => (
        <div>
          <p className="font-[600]">{params.row?.user_name || "-"}</p>
          <p className="text-xs text-[#475467]">{params.row?.user_email}</p>
        </div>
      ),
    },
    {
      field: "symbol",
      headerName: "Stock",
      width: 130,
      renderCell: (params) => params.row?.symbol || "-",
    },
    {
      field: "net_volume_at_cutoff",
      headerName: "Net Volume @ Cutoff",
      width: 170,
      renderCell: (params) =>
        params.row?.net_volume_at_cutoff != null
          ? Number(params.row.net_volume_at_cutoff).toLocaleString()
          : "-",
    },
    {
      field: "dividend_amount",
      headerName: "Dividend Amount (₦)",
      width: 150,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.amount),
    },
    {
      field: "purification_amount",
      headerName: "Purification",
      width: 140,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.purification_amount),
    },
    // {
    //   field: "amount",
    //   headerName: "Net Amount",
    //   width: 150,
    //   renderCell: (params) =>
    //     formatMoney(params.row?.currency, params.row?.amount),
    // },
    {
      field: "cut_off_date",
      headerName: "Cut-off Date",
      width: 130,
      renderCell: (params) => params.row?.cut_off_date || "-",
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <div
          style={{
            display: "inline-block",
            padding: "0 8px",
            height: "22px",
            borderRadius: "10px",
            textAlign: "center",
            lineHeight: "22px",
            textTransform: "capitalize",
            ...dividendStatusStyle(params.row?.status),
          }}
        >
          {params.row?.status || "-"}
        </div>
      ),
    },
    {
      field: "transaction_id",
      headerName: "Transaction ID",
      width: 190,
      renderCell: (params) => params.row?.transaction_id || "-",
    },
    {
      field: "credited_at",
      headerName: "Credited At",
      width: 200,
      renderCell: (params) =>
        params.row?.credited_at
          ? new Date(params.row.credited_at).toLocaleString()
          : "-",
    },
  ];

  const holdingStatusStyle = (status) => {
    const value = (status || "").toLowerCase();
    if (value === "holding" || value === "never_sold") {
      return { color: "#067647", backgroundColor: "#ECFDF3" };
    }
    if (value === "sold_off") {
      return { color: "#B42318", backgroundColor: "#FECDCA" };
    }
    return { color: "#B54708", backgroundColor: "#FFFAEB" };
  };

  const investorColumns = [
    {
      field: "user_name",
      headerName: "User",
      width: 220,
      renderCell: (params) => (
        <div>
          <p className="font-[600]">{params.row?.user_name || "-"}</p>
          <p className="text-xs text-[#475467]">{params.row?.user_email}</p>
        </div>
      ),
    },
    {
      field: "units_held",
      headerName: "Units Held",
      width: 110,
      renderCell: (params) =>
        params.row?.units_held != null
          ? Number(params.row.units_held).toLocaleString()
          : "-",
    },
    {
      field: "units_bought",
      headerName: "Units Bought",
      width: 120,
      renderCell: (params) =>
        params.row?.units_bought != null
          ? Number(params.row.units_bought).toLocaleString()
          : "-",
    },
    {
      field: "units_sold",
      headerName: "Units Sold",
      width: 110,
      renderCell: (params) =>
        params.row?.units_sold != null
          ? Number(params.row.units_sold).toLocaleString()
          : "-",
    },
    {
      field: "average_cost",
      headerName: "Average Cost",
      width: 140,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.average_cost),
    },
    {
      field: "total_invested",
      headerName: "Total Invested",
      width: 150,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.total_invested),
    },
    {
      field: "total_invested_before_selling",
      headerName: "Total Invested Before Selling",
      width: 190,
      renderCell: (params) =>
        formatMoney(
          params.row?.currency,
          params.row?.total_invested_before_selling,
        ),
    },
    {
      field: "total_amount_sold",
      headerName: "Amount Sold",
      width: 150,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.total_amount_sold),
    },
    {
      field: "current_market_value",
      headerName: "Market Value",
      width: 150,
      renderCell: (params) =>
        formatMoney(params.row?.currency, params.row?.current_market_value),
    },
    {
      field: "profit_loss",
      headerName: "Profit / Loss",
      width: 160,
      renderCell: (params) => {
        const value = params.row?.profit_loss;
        const pct = params.row?.profit_loss_pct;
        const positive = Number(value) >= 0;
        return (
          <span style={{ color: positive ? "#067647" : "#B42318" }}>
            {formatMoney(params.row?.currency, value)}
            {pct != null ? ` (${pct}%)` : ""}
          </span>
        );
      },
    },
    {
      field: "holding_status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <div
          style={{
            display: "inline-block",
            padding: "0 8px",
            height: "22px",
            borderRadius: "10px",
            textAlign: "center",
            lineHeight: "22px",
            textTransform: "capitalize",
            ...holdingStatusStyle(params.row?.holding_status),
          }}
        >
          {(params.row?.holding_status || "-").replace(/_/g, " ")}
        </div>
      ),
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 160,
      renderCell: (params) =>
        params.row?.created_at
          ? new Date(params.row.created_at).toLocaleDateString()
          : "-",
    },
  ];

  const priceHistoryCsvHeaders = [
    "ID",
    "Price",
    "% Change",
    "Trend",
    "Available Volume",
    "Side",
    "Donation",
    "Recorded At",
  ];

  const priceHistoryCsvRows = priceHistory.map((row) => [
    row?.id,
    row?.price != null
      ? `${currencySymbol} ${Number(row.price).toFixed(2)}`
      : `${currencySymbol} -`,
    `${Number(row?.percent_change ?? 0).toFixed(2)}%`,
    row?.trend || "-",
    row?.available_volume ?? "-",
    row?.side || "-",
    row?.side === "sell" ? 0 : "N/A",
    row?.recorded_at ? new Date(row.recorded_at).toLocaleString() : "-",
  ]);

  const investorsCsvHeaders = [
    "User",
    "Email",
    "Units Held",
    "Units Bought",
    "Units Sold",
    "Average Cost",
    "Total Invested",
    "Before Selling",
    "Amount Sold",
    "Market Value",
    "Profit / Loss",
    "Profit / Loss %",
    "Status",
    "Date",
  ];

  const investorsCsvRows = stockInvestors.map((row) => [
    row?.user_name || "-",
    row?.user_email || "-",
    row?.units_held ?? "-",
    row?.units_bought ?? "-",
    row?.units_sold ?? "-",
    formatMoney(row?.currency, row?.average_cost),
    formatMoney(row?.currency, row?.total_invested),
    formatMoney(row?.currency, row?.total_invested_before_selling),
    formatMoney(row?.currency, row?.total_amount_sold),
    formatMoney(row?.currency, row?.current_market_value),
    formatMoney(row?.currency, row?.profit_loss),
    row?.profit_loss_pct != null ? `${row.profit_loss_pct}%` : "-",
    (row?.holding_status || "-").replace(/_/g, " "),
    row?.created_at ? new Date(row.created_at).toLocaleDateString() : "-",
  ]);

  const dividendCsvHeaders = [
    "ID",
    "User",
    "Email",
    "Stock",
    "Net Volume @ Cutoff",
    "Dividend Amount",
    "Purification",
    "Cut-off Date",
    "Status",
    "Transaction ID",
    "Credited At",
  ];

  const dividendCsvRows = dividendPayouts.map((row) => [
    row?.id,
    row?.user_name || "-",
    row?.user_email || "-",
    row?.symbol || "-",
    row?.net_volume_at_cutoff ?? "-",
    formatMoney(row?.currency, row?.amount),
    formatMoney(row?.currency, row?.purification_amount),
    row?.cut_off_date || "-",
    row?.status || "-",
    row?.transaction_id || "-",
    row?.credited_at ? new Date(row.credited_at).toLocaleString() : "-",
  ]);

  const historyTabs = [
    { id: "price", name: "Price History" },
    { id: "orders", name: "Orders" },
    { id: "dividend", name: "Dividend History" },
  ];

  return (
    <DashboardLayout>
      <div className="px-2 lg:px-8">
        <div className="flex items-center mb-8 flex-row md:items-center">
          <Link to={"/dashboard"}>
            <img src="/Assets/bread.png" alt="" className="w-8 h-8 mr-2" />
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 mr-2" />
          <Link
            to="/stocks"
            className="font-semibold text-[#475467] text-base flex"
          >
            Stocks
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 md:mr-2" />
          <span className="font-semibold text-[#475467] text-base bg-[#F9FAFB] p-1 px-3 rounded-md">
            {stock?.symbol || "Detail"}
          </span>
        </div>

        <BackArrow2 mb={20} ml={0} />

        {loading && !stock ? (
          <Loader />
        ) : (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <div className="flex items-center gap-4">
                {stock?.logo_url && (
                  <img
                    src={stock.logo_url}
                    alt={stock?.symbol}
                    className="w-14 h-14 rounded-full object-contain border border-[#EAECF0]"
                  />
                )}
                <div>
                  <h1 className="font-semibold text-2xl">
                    {stock?.name} ({stock?.symbol})
                  </h1>
                  <p className="text-[#475467] text-sm">
                    {stock?.exchange || "—"} ·{" "}
                    <span
                      style={{
                        color: stock?.is_active ? "#067647" : "#B42318",
                      }}
                    >
                      {stock?.is_active ? "Active" : "Inactive"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Edit Stock"
                  width="120px"
                  height="42px"
                  borderColor="#D0D5DD"
                  color="#344054"
                  onClick={() => navigate(`/stocks/edit/${id}`)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Invest for User"
                  width="150px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowPlaceOrder(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#B42318"
                  children="Sell for User"
                  width="140px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowSellOrder(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Update Price"
                  width="140px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowUpdatePrice(true)}
                />
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Configure Purification"
                  width="190px"
                  height="42px"
                  borderColor="#D0D5DD"
                  color="#344054"
                  onClick={() => setShowPurification(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  children="Pay Dividend"
                  width="140px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowPayDividend(true)}
                />
                <CustomButton
                  variant="contained"
                  customColor={stock?.is_active ? "#B42318" : "#067647"}
                  children={stock?.is_active ? "Deactivate" : "Activate"}
                  width="130px"
                  height="42px"
                  color="#fff"
                  onClick={() => setShowToggle(true)}
                />
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Delete Stock"
                  width="130px"
                  height="42px"
                  borderColor="#FDA29B"
                  color="#B42318"
                  onClick={() => setShowDelete(true)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <DetailItem
                label="Current Price"
                value={`${currencySymbol} ${stock?.current_price ?? "-"}`}
              />
              <DetailItem
                label="Previous Close"
                value={
                  stock?.previous_close != null
                    ? `${currencySymbol} ${stock.previous_close}`
                    : "-"
                }
              />
              <DetailItem
                label="% Change"
                value={`${stock?.percent_change ?? 0}%`}
              />
              <DetailItem label="Trend" value={stock?.trend} />
              <DetailItem
                label="Available Volume"
                value={stock?.available_volume}
              />
              <DetailItem label="Minimum Volume" value={stock?.min_volume} />
              <DetailItem label="Currency" value={stock?.currency} />
              <DetailItem label="Sector" value={stock?.sector} />
              <DetailItem
                label="Shariah Compliant"
                value={stock?.is_shariah_compliant ? "Yes" : "No"}
              />
              <DetailItem label="Exchange" value={stock?.exchange} />
              <DetailItem
                label="Purification Required"
                value={stock?.purification_required ? "Yes" : "No"}
              />
              <DetailItem
                label="Purification Rate"
                value={
                  stock?.purification_rate != null
                    ? `${stock.purification_rate}%`
                    : "-"
                }
              />
            </div>

            {stock?.description && (
              <div className="mb-8">
                <h2 className="font-[600] text-base mb-2">Description</h2>
                <p className="text-[#475467] text-sm border border-[#EAECF0] rounded-lg p-4">
                  {stock.description}
                </p>
              </div>
            )}

            {stock?.fees && (
              <div className="mb-8">
                <h2 className="font-[600] text-base mb-3">Fees</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.keys(feeLabels).map((key) => (
                    <DetailItem
                      key={key}
                      label={feeLabels[key]}
                      value={stock.fees?.[key]}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mb-8">
              <div className="mb-4 w-full overflow-x-auto">
                <div className="w-[max-content] flex items-center border-b border-b-[#EAECF0]">
                  {historyTabs.map((tab) => (
                    <span
                      key={tab.id}
                      className={`${
                        historyTab === tab.id
                          ? "border-b-2 border-[#128591] text-[#128591] font-[600]"
                          : "text-gray-700"
                      } mr-6 text-base pb-2 cursor-pointer`}
                      onClick={() => setHistoryTab(tab.id)}
                    >
                      {tab.name}
                    </span>
                  ))}
                </div>
              </div>

              {historyTab === "price" ? (
                <>
                  <div className="flex justify-end mb-3">
                    <ExportCsvTable
                      headers={priceHistoryCsvHeaders}
                      rows={priceHistoryCsvRows}
                      filename={`${stock?.symbol || "Stock"}-Price-History.csv`}
                    />
                  </div>
                  {priceHistoryPending && priceHistory?.length < 1 ? (
                    <Loader />
                  ) : (
                    <Box sx={{ overflowX: "auto", width: "100%" }}>
                      <GridTable
                        rows={priceHistory}
                        columns={historyColumns}
                        type={"stock-price-history"}
                        noReport
                        noSearch
                      />
                    </Box>
                  )}
                </>
              ) : historyTab === "orders" ? (
                <>
                  {stockInvestorsSummary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <DetailItem
                        label="Total Investors"
                        value={stockInvestorsSummary?.total}
                      />
                      <DetailItem
                        label="Currently Holding"
                        value={stockInvestorsSummary?.holding}
                      />
                      <DetailItem
                        label="Sold Off"
                        value={stockInvestorsSummary?.sold_off}
                      />
                      <DetailItem
                        label="Never Sold"
                        value={stockInvestorsSummary?.never_sold}
                      />
                    </div>
                  )}
                  <div className="flex justify-between items-center gap-3 mb-3">
                    <DebouncedSearchInput
                      placeholder="Search by user name"
                      onSearch={(term) =>
                        dispatch(
                          getStockInvestors({
                            id,
                            ...(term ? { search: term } : {}),
                          }),
                        )
                      }
                    />
                    <ExportCsvTable
                      headers={investorsCsvHeaders}
                      rows={investorsCsvRows}
                      filename={`${stock?.symbol || "Stock"}-Investors.csv`}
                    />
                  </div>
                  {stockInvestorsPending && stockInvestors?.length < 1 ? (
                    <Loader />
                  ) : (
                    <Box sx={{ overflowX: "auto", width: "100%" }}>
                      <GridTable
                        rows={stockInvestors.map((row) => ({
                          ...row,
                          id: row?.user_id,
                        }))}
                        columns={investorColumns}
                        type={"stock-investors"}
                        noReport
                        noSearch
                      />
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center gap-3 mb-3">
                    <DebouncedSearchInput
                      placeholder="Search by user name"
                      onSearch={(term) =>
                        dispatch(
                          getStockDividendPayouts({
                            id,
                            ...(term ? { search: term } : {}),
                          }),
                        )
                      }
                    />
                    <ExportCsvTable
                      headers={dividendCsvHeaders}
                      rows={dividendCsvRows}
                      filename={`${
                        stock?.symbol || "Stock"
                      }-Dividend-History.csv`}
                    />
                  </div>
                  {dividendPayoutsPending && dividendPayouts?.length < 1 ? (
                    <Loader />
                  ) : (
                    <Box sx={{ overflowX: "auto", width: "100%" }}>
                      <GridTable
                        rows={dividendPayouts}
                        columns={dividendColumns}
                        type={"stock-dividend-history"}
                        noReport
                        noSearch
                      />
                    </Box>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <UpdateStockPriceModal
        open={showUpdatePrice}
        stock={stock}
        onClose={() => setShowUpdatePrice(false)}
      />
      <ConfigureStockPurificationModal
        open={showPurification}
        stock={stock}
        onClose={() => setShowPurification(false)}
      />
      <PayDividendModal
        open={showPayDividend}
        stock={stock}
        onClose={() => setShowPayDividend(false)}
        onSuccess={() => {
          dispatch(getStockDividendPayouts({ id }));
          dispatch(getStockInvestors({ id }));
        }}
      />
      <ToggleStockActiveModal
        open={showToggle}
        stock={stock}
        onClose={() => setShowToggle(false)}
      />
      <DeleteStockModal
        open={showDelete}
        stock={stock}
        onClose={() => setShowDelete(false)}
      />
      <PlaceOrderForUserModal
        open={showPlaceOrder}
        stock={stock}
        onClose={() => setShowPlaceOrder(false)}
      />
      <SellStockForUserModal
        open={showSellOrder}
        stock={stock}
        onClose={() => setShowSellOrder(false)}
      />
    </DashboardLayout>
  );
}

export default StockDetail;
